import { useState, useEffect, useCallback } from 'react';
import { FunctionInvokeOptions } from '@supabase/supabase-js';
import { Todo, NewTodo, TodoWithState } from '../types/todos';
import { supabase } from '../services/supabase/client.ts';

export function useTodos() {
    const [todos, setTodos] = useState<TodoWithState[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch all todos
    const fetchTodos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const { data, error } = await supabase
                .functions.invoke('todos', {
                    method: 'GET',
                });

            if (error) throw new Error(error.message);
            setTodos(data?.data || []);
        } catch (err) {
            console.error('Error fetching todos:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch todos');
        } finally {
            setLoading(false);
        }
    }, []);

    // Add a new todo
    const addTodo = useCallback(async (newTodo: NewTodo) => {
        try {
            setError(null);

            const { data, error } = await supabase
                .functions.invoke('todos', {
                    method: 'POST',
                    body: { data: newTodo },
                });

            if (error) throw new Error(error.message);

            if (data?.data) {
                setTodos(prev => [data.data as Todo, ...prev]);
            }

            return data?.data as Todo;
        } catch (err) {
            console.error('Error adding todo:', err);
            setError(err instanceof Error ? err.message : 'Failed to add todo');
            throw err;
        }
    }, []);

    // Update a todo
    const updateTodo = useCallback(async (id: string, updates: Partial<Todo>) => {
        try {
            setError(null);

            // Optimistic update
            setTodos(prev =>
                prev.map(todo =>
                    todo.id === id
                        ? { ...todo, ...updates, isLoading: true }
                        : todo
                )
            );

            const { data, error } = await supabase
                .functions.invoke('todos', <FunctionInvokeOptions> {
                    method: 'PUT',
                    body: { data: updates, id: id }
                });
            if (error) throw new Error(error.message);

            // Update with actual server data
            setTodos(prev =>
                prev.map(todo =>
                    todo.id === id
                        ? { ...(data?.data as Todo), isLoading: false }
                        : todo
                )
            );

            return data?.data as Todo;
        } catch (err) {
            console.error('Error updating todo:', err);
            setError(err instanceof Error ? err.message : 'Failed to update todo');

            // Revert optimistic update on error
            fetchTodos();
            throw err;
        }
    }, [fetchTodos]);

    // Toggle todo completion status
    const toggleTodo = useCallback(async (id: string, currentStatus: boolean) => {
        return updateTodo(id, { is_complete: !currentStatus });
    }, [updateTodo]);

    // Delete a todo
    const deleteTodo = useCallback(async (id: string) => {
        try {
            setError(null);

            // Optimistic delete
            setTodos(prev => prev.filter(todo => todo.id !== id));

            const { error } = await supabase
                .functions.invoke('todos', {
                    method: 'DELETE',
                    body: { id: id }
                });

            if (error) throw new Error(error.message);
        } catch (err) {
            console.error('Error deleting todo:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete todo');

            // Revert optimistic delete on error
            fetchTodos();
            throw err;
        }
    }, [fetchTodos]);

    // Load todos on component mount
    useEffect(() => {
        fetchTodos();
    }, [fetchTodos]);

    // Subscribe to changes (real-time updates)
    useEffect(() => {
        const subscription = supabase
            .channel('public:todos')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'todos' }, () => {
                fetchTodos();
            })
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [fetchTodos]);

    return {
        todos,
        loading,
        error,
        addTodo,
        updateTodo,
        toggleTodo,
        deleteTodo,
        refreshTodos: fetchTodos
    };
}